import React, { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthenticatedClientInfoFields from '../components/AuthenticatedClientInfoFields';
import BookingConfirmationModal from '../components/BookingConfirmationModal';
import { useAuthenticatedBookingProfile } from '../hooks/useAuthenticatedBookingProfile';
import { createBookingRequest } from '../lib/bookingService';
import { formatBudgetRange, handleBudgetKeyDown, normalizeBudgetInput, validateBudgetRange } from '../lib/budget';
import { focusFirstInvalidField } from '../lib/formErrors';
import { validatePhilippineMobile } from '../lib/phoneNumber';

const FieldError = ({ message }) => (
  message ? <p className="text-xs text-rose-300 mt-1">{message}</p> : null
);

const CORPORATE_EVENT_TYPES = [
  'Product Launch',
  'Seminar',
  'Networking Event',
  'Team Building',
  'Conference',
  'Trade Show',
  'Workshop',
  'Other',
];

export default function CorporateRequestPage() {
  const navigate = useNavigate();
  const { name: requesterName, email: requesterEmail, phone: profilePhone, loading: profileLoading } = useAuthenticatedBookingProfile();
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [createdRequestCode, setCreatedRequestCode] = useState('');
  const [createdBookingId, setCreatedBookingId] = useState('');
  const [venueChoice, setVenueChoice] = useState('let_echo_jham');
  const [formData, setFormData] = useState({
    booking_type: 'corporate',
    event_name: '',
    budget_min: '',
    budget_max: '',
    theme: '',
    venue: '',
    event_date: '',
    attendee_count: '',
    event_type: '',
    event_type_other: '',
    specific_needs: '',
    details: '',
    requester_phone: '',
    company_name: '',
  });

  useEffect(() => {
    if (!profilePhone) return;
    setFormData((prev) => (
      prev.requester_phone === profilePhone ? prev : { ...prev, requester_phone: profilePhone }
    ));
  }, [profilePhone]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};

    const phoneError = validatePhilippineMobile(formData.requester_phone);
    if (phoneError) nextErrors.requester_phone = phoneError;

    if (!String(formData.event_name || '').trim()) {
      nextErrors.event_name = 'Please enter an event name.';
    }

    if (!String(formData.event_type || '').trim()) {
      nextErrors.event_type = 'Please select an event type.';
    }

    if (formData.event_type === 'other' && !String(formData.event_type_other || '').trim()) {
      nextErrors.event_type_other = 'Please specify your event type.';
    }

    const budget = validateBudgetRange(formData.budget_min, formData.budget_max);
    if (budget.error) {
      nextErrors[budget.field || 'budget_min'] = budget.error;
    }

    if (venueChoice === 'we_have_one' && !String(formData.venue || '').trim()) {
      nextErrors.venue = 'Please specify the venue.';
    }

    if (!requesterEmail) {
      nextErrors.requester_email = 'Please sign in again to submit a booking.';
    }

    const firstInvalid = [
      'requester_phone',
      'event_name',
      'event_type',
      'event_type_other',
      'budget_min',
      'budget_max',
      'venue',
      'requester_email',
    ].find((field) => nextErrors[field]);
    if (firstInvalid) {
      setFieldErrors(nextErrors);
      focusFirstInvalidField(firstInvalid);
      return;
    }

    setFieldErrors({});
    setLoading(true);
    try {
      const payload = {
        ...formData,
        event_type: formData.event_type === 'other' ? formData.event_type_other.trim() : formData.event_type,
        budget_range: formatBudgetRange(budget.min, budget.max),
        event_date: '',
        venue: venueChoice === 'we_have_one' ? formData.venue : '',
      };

      const booking = await createBookingRequest(payload);
      setCreatedRequestCode(booking.request_code || '');
      setCreatedBookingId(booking.id || '');
      setShowConfirmation(true);
    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Error submitting form');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));

    if (name === 'budget_min' || name === 'budget_max') {
      setFormData((prev) => ({
        ...prev,
        [name]: normalizeBudgetInput(value),
      }));
      return;
    }

    if (name === 'event_type') {
      setFormData((prev) => ({
        ...prev,
        event_type: value,
        event_type_other: value === 'other' ? prev.event_type_other : '',
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const goToCreatedBooking = () => {
    setShowConfirmation(false);
    navigate(createdBookingId ? `/book/progress/${createdBookingId}` : '/book');
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    navigate('/book');
  };

  return (
    <div className="max-w-3xl mx-auto">
      <button
        onClick={() => navigate('/book')}
        className="flex items-center gap-2 text-slate-300 hover:text-white transition mb-5 text-sm"
      >
        <ArrowLeft size={18} />
      </button>

      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Corporate Event Booking</h1>
          <p className="text-sm text-slate-400">Provide details for your event, and we&apos;ll handle the rest.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-transparent" noValidate>
          <AuthenticatedClientInfoFields
            name={requesterName}
            email={requesterEmail}
            phone={profilePhone}
            loading={profileLoading}
          />
          {fieldErrors.requester_email ? <FieldError message={fieldErrors.requester_email} /> : null}
          <div className="grid md:grid-cols-2 gap-4">
            {!profilePhone ? (
            <div>
              <label>Contact Number</label>
              <input
                type="tel"
                name="requester_phone"
                value={formData.requester_phone}
                onChange={handleChange}
                placeholder="Optional — 09XXXXXXXXX or +639XXXXXXXXX"
                aria-invalid={Boolean(fieldErrors.requester_phone)}
              />
              <FieldError message={fieldErrors.requester_phone} />
              <p className="mt-1 text-xs text-slate-400">
                Save a mobile number in Profile Settings to skip this on later bookings.
              </p>
            </div>
            ) : null}
            <div>
              <label>Company Name</label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Optional — shown as the client company"
              />
            </div>
          </div>

          {/* Event Basics */}
          <fieldset className="space-y-4 pb-6 border-b border-white/10">
            <legend className="text-base font-bold text-white">Event Basics</legend>
            <div>
              <label>Event Name *</label>
              <input
                type="text"
                name="event_name"
                value={formData.event_name}
                onChange={handleChange}
                required
                placeholder="e.g., Annual Sales Conference 2026"
                aria-invalid={Boolean(fieldErrors.event_name)}
              />
              <FieldError message={fieldErrors.event_name} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label>Event Type *</label>
                <select
                  name="event_type"
                  value={formData.event_type}
                  onChange={handleChange}
                  required
                  aria-invalid={Boolean(fieldErrors.event_type)}
                >
                  <option value="" disabled>Select event type</option>
                  {CORPORATE_EVENT_TYPES.map(type => (
                    <option key={type} value={type.toLowerCase()}>{type}</option>
                  ))}
                </select>
                <FieldError message={fieldErrors.event_type} />
              </div>
              {formData.event_type === 'other' && (
                <div>
                  <label>Please specify: *</label>
                  <input
                    type="text"
                    name="event_type_other"
                    value={formData.event_type_other}
                    onChange={handleChange}
                    required
                    placeholder="Please specify"
                    aria-invalid={Boolean(fieldErrors.event_type_other)}
                  />
                  <FieldError message={fieldErrors.event_type_other} />
                </div>
              )}
            </div>
          </fieldset>

          {/* Details */}
          <fieldset className="space-y-4 pb-6 border-b border-white/10">
            <legend className="text-base font-bold text-white">Event Details</legend>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label>Number of Attendees</label>
                <input
                  type="number"
                  name="attendee_count"
                  value={formData.attendee_count}
                  onChange={handleChange}
                  min="1"
                  placeholder="Expected number"
                />
              </div>
              <div>
                <label>Budget Range *</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    name="budget_min"
                    value={formData.budget_min}
                    onChange={handleChange}
                    onKeyDown={handleBudgetKeyDown}
                    required
                    placeholder="From: 500000"
                    aria-invalid={Boolean(fieldErrors.budget_min)}
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    name="budget_max"
                    value={formData.budget_max}
                    onChange={handleChange}
                    onKeyDown={handleBudgetKeyDown}
                    required
                    placeholder="To: 750000"
                    aria-invalid={Boolean(fieldErrors.budget_max || fieldErrors.budget_min)}
                  />
                </div>
                <FieldError message={fieldErrors.budget_min || fieldErrors.budget_max} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label>Theme</label>
                <input
                  type="text"
                  name="theme"
                  value={formData.theme}
                  onChange={handleChange}
                  placeholder="e.g., Innovation & Growth"
                />
              </div>
              <div>
                <label>Venue Location</label>
                <label className="flex items-center gap-2 cursor-pointer text-slate-300 mb-1">
                  <input
                    type="radio"
                    name="venue_choice"
                    value="let_echo_jham"
                    checked={venueChoice === 'let_echo_jham'}
                    onChange={(e) => setVenueChoice(e.target.value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Let EchoJham provide</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-red-400 mb-2">
                  <input
                    type="radio"
                    name="venue_choice"
                    value="we_have_one"
                    checked={venueChoice === 'we_have_one'}
                    onChange={(e) => setVenueChoice(e.target.value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Do you have a venue in mind? Please Specify:</span>
                </label>

                {venueChoice === 'we_have_one' && (
                  <>
                    <input
                      type="text"
                      id="venue"
                      name="venue"
                      value={formData.venue}
                      onChange={handleChange}
                      required={venueChoice === 'we_have_one'}
                      placeholder="Specify here"
                      aria-invalid={Boolean(fieldErrors.venue)}
                    />
                    <FieldError message={fieldErrors.venue} />
                  </>
                )}
              </div>
            </div>
          </fieldset>

          {/* Special Needs */}
          <fieldset className="space-y-4">
            <legend className="text-base font-bold text-white">Special Needs & Details</legend>
            <div>
              <label>Specific Requirements</label>
              <textarea
                name="specific_needs"
                value={formData.specific_needs}
                onChange={handleChange}
                rows="3"
                placeholder="e.g., AV equipment, catering, specific seating arrangement, etc."
              />
            </div>
            <div>
              <label>Event Details & Other Requirements</label>
              <textarea
                name="details"
                value={formData.details}
                onChange={handleChange}
                rows="5"
                placeholder="Detailed description of your event, goals, and any other relevant information..."
              />
            </div>
          </fieldset>

          {/* Submit */}
          <div className="flex gap-4 pt-6 border-t border-white/10">
            <button
              type="button"
              onClick={() => navigate('/book')}
              className="flex-1 py-3 rounded-xl font-semibold border border-white/20 text-slate-200 hover:bg-white/10"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || profileLoading || !requesterEmail}
              className="flex-1 py-3 rounded-xl font-semibold bg-[#8b0000] hover:bg-[#6f0000] text-white disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Corporate Request'}
            </button>
          </div>
        </form>
      </div>

      <BookingConfirmationModal
        open={showConfirmation}
        requestCode={createdRequestCode}
        onClose={handleCloseConfirmation}
        onViewProcess={goToCreatedBooking}
      />
    </div>
  );
}
